// API Service — capa de conexión al backend.
//
// Este archivo es la ÚNICA capa que sabe de HTTP. El resto del frontend
// consume esto vía articleService.getAll(), categoryService.getBySlug(),
// etc. La forma pública de los objetos devueltos (title, excerpt, author,
// category, publishedAt, imageUrl, views…) se mantiene idéntica a la del
// prototipo original — internamente traducimos los campos del .NET
// (titulo, resumen, autores, categoria, publicadoEn, heroImagenUrl,
// vistas…) para que ningún componente React tenga que cambiar.
//
// Backends soportados (vía flag USE_MOCK):
//   • Datos mock locales (mockData.js) para desarrollo sin backend.
//   • Backend .NET Core (Tracker.Api) con Postgres en GCP Cloud SQL,
//     autenticación JWT Ecolab, multi-tenant por periodico_id.
//
// Variables de entorno esperadas (.env local, .env.production, etc.):
//   REACT_APP_API_URL        Base del backend .NET, ej: http://localhost:5000
//                            (el prefijo /api/v1 se agrega aquí, no en la env)
//   REACT_APP_USE_MOCK       'false' para hablar con el backend real
//   REACT_APP_PERIODICO_ID   UUID del periódico (tenant). El front actual
//                            asume UN solo periódico visible; ese UUID va
//                            aquí y se usa para armar todas las rutas.
//   REACT_APP_STORAGE_BUCKET Nombre del bucket de Firebase Storage
//                            (para transformar gs:// → https://)

import { articles, categories, authors, editions, newspaperConfig, advertising } from '../data/mockData';

const API_BASE_URL   = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';
const USE_MOCK       = process.env.REACT_APP_USE_MOCK !== 'false';
// PERIODICO_ID: si el .env NO lo especifica, se resuelve en runtime
// llamando /periodicos/default (ver resolvePeriodicoId más abajo).
// Antes había aquí un UUID hardcoded 'aaaaaaaa-…' que era un mal patrón
// (obligaba a hardcodear un valor de BD en el código).
let PERIODICO_ID   = process.env.REACT_APP_PERIODICO_ID || null;
const STORAGE_BUCKET = process.env.REACT_APP_STORAGE_BUCKET || 'news-media-a28cd.firebasestorage.app';
const ECOLAB_API_URL = process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';

// Simular delay de red (solo para mock)
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ═══════════════════════════════════════════════════════════════════════
//  Refresh automático de JWT ante 401
// ═══════════════════════════════════════════════════════════════════════
// Cuando el JWT expira, cualquier request al backend .NET devuelve 401.
// En vez de forzar al usuario a re-loguearse, intentamos silenciosamente
// canjear el refreshToken (que Ecolab devuelve al login inicial) por un
// nuevo accessToken, y reintentamos la request original UNA vez.
//
// Un solo refresh en curso a la vez: si varias requests fallan con 401
// simultáneamente, todas esperan la MISMA promesa de refresh, no
// disparan N intentos que se pisen entre sí.
let _refreshInFlight = null;

const _tryRefreshToken = async () => {
  const refreshToken = localStorage.getItem('newspaperRefreshToken');
  if (!refreshToken) return false;
  try {
    const resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!resp.ok) {
      // Refresh token vencido o inválido → limpiar todo y forzar login.
      localStorage.removeItem('newspaperToken');
      localStorage.removeItem('newspaperRefreshToken');
      localStorage.removeItem('newspaperUser');
      return false;
    }
    const data = await resp.json();
    const newToken   = data.accessToken || data.access_token || data.token || data.jwt;
    const newRefresh = data.refreshToken || data.refresh_token;
    if (!newToken) return false;
    localStorage.setItem('newspaperToken', newToken);
    if (newRefresh) localStorage.setItem('newspaperRefreshToken', newRefresh);
    return true;
  } catch {
    return false;
  }
};

const ensureFreshToken = () => {
  // Serializa: si ya hay un refresh en curso, reusar esa promesa.
  if (!_refreshInFlight) {
    _refreshInFlight = _tryRefreshToken().finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
};

// ═══════════════════════════════════════════════════════════════════════
//  Resolver del periódico activo (tenant)
// ═══════════════════════════════════════════════════════════════════════
// Reglas:
//   1. Si el .env definió REACT_APP_PERIODICO_ID, esa gana (útil para
//      múltiples despliegues del frontend, uno por región).
//   2. Si no, el frontend pregunta al backend cuál es el periódico
//      marcado como es_por_defecto=TRUE y usa ese.
//   3. Si el backend no tiene ninguno marcado (setup incompleto), se
//      lanza un error explícito — es mejor fallar temprano que armar
//      URLs con undefined.
// La promesa se cachea: solo se pregunta al backend una vez por sesión.
let _periodicoIdPromise = null;

const resolvePeriodicoId = () => {
  if (PERIODICO_ID) return Promise.resolve(PERIODICO_ID);
  if (!_periodicoIdPromise) {
    _periodicoIdPromise = fetchApi('/periodicos/default')
      .then(p => {
        if (!p?.id) {
          throw new Error(
            'No hay periódico por defecto configurado. ' +
            'Marca uno con es_por_defecto=TRUE en la BD o desde el CMS.');
        }
        PERIODICO_ID = p.id;
        return p.id;
      })
      .catch(err => {
        _periodicoIdPromise = null;  // permitir reintento
        throw err;
      });
  }
  return _periodicoIdPromise;
};

// ═══════════════════════════════════════════════════════════════════════
//  HTTP helper
// ═══════════════════════════════════════════════════════════════════════
const _doFetch = async (endpoint, options) => {
  const url   = `${API_BASE_URL}${endpoint}`;
  const token = localStorage.getItem('newspaperToken');
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  };
  return fetch(url, { ...defaultOptions, ...options });
};

const fetchApi = async (endpoint, options = {}) => {
  // Guardia lazy: si un componente hizo su primera llamada ANTES de que
  // el bootstrap del AppContext resolviera el PERIODICO_ID (típico en
  // deep-links: usuario recarga en /seccion/xxx), la URL llega con
  // "/periodicos/null/...". Resolvemos y reescribimos sin romper nada.
  if (endpoint.includes('/periodicos/null')) {
    await resolvePeriodicoId();
    endpoint = endpoint.replace('/periodicos/null', `/periodicos/${PERIODICO_ID}`);
  }

  let response = await _doFetch(endpoint, options);

  // JWT expirado → intentar refresh una sola vez y reintentar.
  // Solo tiene sentido si teníamos token (si no había token el 401 no
  // se resuelve con refresh) y si hay refreshToken guardado.
  if (response.status === 401
      && localStorage.getItem('newspaperToken')
      && localStorage.getItem('newspaperRefreshToken')) {
    const refreshed = await ensureFreshToken();
    if (refreshed) {
      response = await _doFetch(endpoint, options);
    }
  }

  // Si tras el intento de refresh sigue 401, o si el refresh falló y no
  // había token: sesión expirada → limpiar y forzar login.
  if (response.status === 401) {
    localStorage.removeItem('newspaperToken');
    localStorage.removeItem('newspaperRefreshToken');
    localStorage.removeItem('newspaperUser');
    // Solo redirigir si no estamos ya en la página de login (evita bucle)
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      const retorno = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?retorno=${retorno}`;
    }
    throw new Error('Sesión expirada. Inicia sesión de nuevo.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || errorData.detail || `API Error: ${response.status}`);
  }

  // 204 No Content no tiene body
  if (response.status === 204) return null;
  return response.json();
};

// ═══════════════════════════════════════════════════════════════════════
//  Adaptadores: traducen shape del backend .NET → shape del frontend
// ═══════════════════════════════════════════════════════════════════════
// Regla: si el backend devuelve gs://bucket/path, lo convertimos a
// https://storage.googleapis.com/bucket/path para que <img src> funcione
// directo con objetos públicos. Objetos privados requerirán signed URLs
// vía /storage/signed-url (a integrar cuando aplique).
const gsToHttps = (gsUri) => {
  if (!gsUri) return null;
  if (!gsUri.startsWith('gs://')) return gsUri;
  const parts = gsUri.substring(5).split('/');
  const bucket = parts.shift();
  const path = parts.join('/');
  return `https://storage.googleapis.com/${bucket}/${path}`;
};

const mapCategoria = (c) => c && ({
  id:          c.id,
  slug:        c.slug,
  name:        c.nombre,
  description: c.descripcion,
  color:       c.color,
  icon:        c.icono,
  order:       c.orden,
  active:      c.activa,
  parentId:    c.parentId,
});

const mapAutor = (a) => a && ({
  id:        a.id,
  slug:      a.slug,
  name:      a.nombre,
  bio:       a.bio,
  avatar:    gsToHttps(a.avatarUrl),
  role:      a.cargo,
  email:     a.emailPublico,
  celular:   a.celular,
  twitter:   a.twitter,
  instagram: a.instagram,
  linkedin:  a.linkedin,
  website:   a.sitioWeb,
  active:    a.activo,
  // Puente autor↔usuario: null si el autor es standalone (colaborador
  // externo), guid del usuarios_cache si está vinculado a un usuario
  // del sistema.
  userId:    a.usuarioId ?? null,
});

const mapTag = (t) => t && ({
  id:   t.id,
  slug: t.slug,
  name: t.nombre,
});

// Mapea la vista de listado (sin cuerpo). El backend puede devolver
// autores[] con nombre "autores"; el frontend histórico espera un
// único "author". Tomamos el primero de la lista para compatibilidad.
const mapArticleList = (a) => a && ({
  id:          a.id,
  slug:        a.slug,
  title:       a.titulo,
  subtitle:    a.subtitulo,
  excerpt:     a.resumen,
  // 'image' es el nombre canónico del prototipo. 'imageUrl' se
  // mantiene como alias para nuevos componentes que prefieran el
  // nombre explícito.
  image:       gsToHttps(a.heroImagenUrl),
  imageUrl:    gsToHttps(a.heroImagenUrl),
  imageAlt:    a.heroImagenAlt,
  type:        a.tipo,
  status:      a.estado,
  publishedAt: a.publicadoEn,
  createdAt:   a.creadoEn,
  // El cuerpo (HTML) solo se envía en endpoints específicos como la
  // vista de revista. En listados regulares viene null y no se
  // renderiza — no es error, es diseño.
  content:     a.cuerpo,
  featured:    a.destacado,
  breaking:    a.esUltimaHora,
  isBreaking:  a.esUltimaHora,  // alias legacy del prototipo (is_breaking)
  readingTime: a.tiempoLecturaMin,
  views:       a.vistas,
  categoryId:  a.categoria?.id,
  category:    mapCategoria(a.categoria),
  authorId:    a.autores?.[0]?.id,
  author:      a.autores?.[0] && {
    id:     a.autores[0].id,
    slug:   a.autores[0].slug,
    name:   a.autores[0].nombre,
    avatar: gsToHttps(a.autores[0].avatarUrl),
  },
  authors: (a.autores || []).map(x => ({
    id: x.id, slug: x.slug, name: x.nombre, avatar: gsToHttps(x.avatarUrl), order: x.orden,
  })),
});

// Mapea el detalle completo (incluye cuerpo, SEO, tags)
const mapArticleDetail = (a) => a && ({
  ...mapArticleList(a),
  content:        a.cuerpo,
  contentFormat:  a.formatoCuerpo,
  imageCredit:    a.heroImagenCredito,
  metaTitle:      a.metaTitulo,
  metaDescription: a.metaDescripcion,
  ogImage:        gsToHttps(a.ogImagenUrl),
  // 'tags' como array de STRINGS (nombres) — es lo que espera el
  // prototipo (ArticlePage renderiza {tag} directo, ArticleEditor
  // hace .join(',') / .split(',')). Si un componente nuevo necesita
  // acceso al id/slug del tag, usar tagObjects.
  tags:           (a.tags || []).map(t => t.nombre),
  tagObjects:     (a.tags || []).map(mapTag),
  // Alias legacy CSV (el SQLite lo guardaba así).
  tagsCsv:        (a.tags || []).map(t => t.nombre).join(','),
});

// Backend devuelve PagedResult { total, pagina, tamano, items }.
// Frontend histórico espera { data, total, page, totalPages }.
const mapPaged = (paged, mapper) => ({
  data:       (paged.items || []).map(mapper),
  total:      paged.total || 0,
  page:       paged.pagina || 1,
  totalPages: paged.tamano ? Math.ceil((paged.total || 0) / paged.tamano) : 1,
});

const mapEdition = (e) => e && ({
  id:          e.id,
  number:      e.numero,
  date:        e.fecha,
  title:       e.titulo,
  pdfUrl:      gsToHttps(e.pdfUrl),
  // 'coverImage' es el nombre canónico del prototipo. 'coverUrl'
  // queda como alias para nuevos componentes.
  coverImage:  gsToHttps(e.portadaUrl),
  coverUrl:    gsToHttps(e.portadaUrl),
  status:      e.estado === 'publicada' ? 'published' : (e.estado === 'archivada' ? 'archived' : 'draft'),
  publishedAt: e.publicadoEn,
  // Edición nacional (fallback si no hay regional para el depto del lector).
  isDefault:   !!e.esPorDefecto,
  /** Total de páginas físicas de la impresa (múltiplo de 4). */
  totalPages:  e.totalPaginas ?? 16,
  // Geografía: snapshot resuelto por el backend cuando se creó/editó la
  // edición contra Ecolab. Sirve para pintar el editor sin volver a
  // pedir catálogos, y para etiquetas tipo "Edición Bucaramanga".
  municipalityId:    e.municipioId ?? null,
  municipalityName:  e.municipioNombre ?? null,
  departmentId:      e.departamentoId ?? null,
  departmentName:    e.departamentoNombre ?? null,
  articles: (e.articulos || []).map((x) => ({
    ...mapArticleList(x.articulo),
    page:  x.pagina,
    order: x.orden,
    // Marcado como portada (hero) para esta edición. Máximo uno por
    // edición. La home lo usa como artículo principal grande.
    isCover: !!x.esPortada,
  })),
  // TRUE cuando el lector pidió edición regional pero el backend
  // sirvió la nacional por falta de contenido regional. El home la
  // usa para mostrar un aviso amigable.
  isFallbackNational: !!e.esFallbackNacional,
});

const mapPeriodico = (p) => p && ({
  id:              p.id,
  slug:            p.slug,
  name:            p.nombre,
  domain:          p.dominio,
  description:     p.descripcion,
  logo:            gsToHttps(p.logoUrl),
  isotype:         gsToHttps(p.isotipoUrl),
  colorPrimary:    p.colorPrimario,
  colorSecondary:  p.colorSecundario,
  cityId:          p.ciudadId,
  active:          p.activo,
});

// ═══════════════════════════════════════════════════════════════════════
//  ARTÍCULOS
// ═══════════════════════════════════════════════════════════════════════
export const articleService = {
  getAll: async (params = {}) => {
    if (!USE_MOCK) {
      // Traducimos params del frontend histórico a query del backend.
      const query = new URLSearchParams();
      if (params.page)     query.set('pagina', params.page);
      if (params.limit)    query.set('tamano', params.limit);
      if (params.search)   query.set('q', params.search);
      // Alias: distintos componentes usan 'category' o 'categorySlug'.
      // Aceptamos ambos y el backend recibe 'categoria' (slug).
      const catSlug = params.categorySlug ?? params.category;
      if (catSlug)         query.set('categoria', catSlug);
      const tagSlug = params.tagSlug ?? params.tag;
      if (tagSlug)         query.set('tag', tagSlug);

      const paged = await fetchApi(`/periodicos/${PERIODICO_ID}/articulos?${query.toString()}`);
      return mapPaged(paged, mapArticleList);
    }

    // ── MOCK (intacto) ──────────────────────────────────────────────
    await delay(100);
    let result = [...articles];
    if (params.categoryId) result = result.filter(a => a.categoryId === parseInt(params.categoryId));
    if (params.authorId)   result = result.filter(a => a.authorId === parseInt(params.authorId));
    if (params.featured)   result = result.filter(a => a.featured);
    if (params.trending)   result = result.filter(a => a.trending);
    if (params.search) {
      const s = params.search.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(s) ||
        a.excerpt.toLowerCase().includes(s) ||
        a.tags.some(t => t.toLowerCase().includes(s)));
    }
    if (params.orderBy === 'views')       result.sort((a, b) => b.views - a.views);
    else if (params.orderBy === 'oldest') result.sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt));
    else                                  result.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

    result = result.map(article => ({
      ...article,
      category: categories.find(c => c.id === article.categoryId),
      author:   authors.find(a => a.id === article.authorId),
    }));
    const page  = params.page || 1;
    const limit = params.limit || 10;
    const start = (page - 1) * limit;
    return {
      data:       result.slice(start, start + limit),
      total:      result.length,
      page,
      totalPages: Math.ceil(result.length / limit),
    };
  },

  getFeatured: async (limit = 5) => {
    if (!USE_MOCK) {
      const list = await fetchApi(`/periodicos/${PERIODICO_ID}/articulos/destacados?limite=${limit}`);
      return list.map(mapArticleList);
    }
    await delay(100);
    return articles.filter(a => a.featured).slice(0, limit).map(article => ({
      ...article,
      category: categories.find(c => c.id === article.categoryId),
      author:   authors.find(a => a.id === article.authorId),
    }));
  },

  // Listado admin: incluye borradores, en revisión y archivados. Acepta
  // filtros opcionales por estado, categoría (slug), autor (slug) y
  // búsqueda libre en título/subtítulo/resumen/cuerpo.
  getAllAdmin: async (params = {}) => {
    if (!USE_MOCK) {
      const query = new URLSearchParams();
      if (params.page)   query.set('pagina', params.page);
      if (params.limit)  query.set('tamano', params.limit);
      if (params.estado) query.set('estado', params.estado);
      if (params.categorySlug ?? params.category)
        query.set('categoria', params.categorySlug ?? params.category);
      if (params.authorSlug ?? params.author)
        query.set('autor', params.authorSlug ?? params.author);
      if (params.search) query.set('q', params.search);

      const paged = await fetchApi(
        `/admin/periodicos/${PERIODICO_ID}/articulos?${query.toString()}`);
      return mapPaged(paged, mapArticleList);
    }
    return articleService.getAll(params);
  },

  /** Artículos en estado en_revision (solo Editor). */
  getRevisionesPendientes: async ({ page = 1, limit = 50 } = {}) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(
      `/admin/periodicos/${PERIODICO_ID}/revisiones-pendientes?pagina=${page}&tamano=${limit}`);
    return mapPaged(paged, mapArticleList);
  },

  getById: async (id) => {
    if (!USE_MOCK) {
      // El backend .NET tiene detalle por id bajo /admin. Para lectura
      // pública se usa slug; si un componente llama por id lo consultamos
      // vía admin (requiere JWT) y si falla intentamos por slug via lookup.
      const a = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos/${id}`);
      return mapArticleDetail(a);
    }
    await delay(50);
    const article = articles.find(a => a.id === parseInt(id));
    if (!article) throw new Error('Artículo no encontrado');
    return {
      ...article,
      category: categories.find(c => c.id === article.categoryId),
      author:   authors.find(a => a.id === article.authorId),
    };
  },

  /** Ediciones donde el artículo está vinculado. Devuelve una lista
   *  liviana con datos suficientes para pintar el modal "ver ediciones".
   *  Ordenada por fecha editorial descendente. Cada item trae:
   *    { editionId, number, title, date, status, isCover,
   *      isDefault, departmentName, municipalityName } */
  listEditions: async (articleId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(
      `/admin/periodicos/${PERIODICO_ID}/articulos/${articleId}/ediciones`);
    return (list || []).map(x => ({
      editionId:        x.edicionId,
      number:           x.numero,
      title:            x.titulo,
      date:             x.fecha,
      status:           x.estado,
      isCover:          !!x.esPortada,
      isDefault:        !!x.esPorDefecto,
      departmentName:   x.departamentoNombre,
      municipalityName: x.municipioNombre,
    }));
  },

  getBySlug: async (slug) => {
    if (!USE_MOCK) {
      const a = await fetchApi(`/periodicos/${PERIODICO_ID}/articulos/slug/${encodeURIComponent(slug)}`);
      return mapArticleDetail(a);
    }
    await delay(50);
    const article = articles.find(a => a.slug === slug);
    if (!article) throw new Error('Artículo no encontrado');
    return {
      ...article,
      category: categories.find(c => c.id === article.categoryId),
      author:   authors.find(a => a.id === article.authorId),
    };
  },

  incrementView: async (id) => {
    if (!USE_MOCK) {
      return fetchApi(`/periodicos/${PERIODICO_ID}/articulos/${id}/view`, { method: 'POST' });
    }
    // sin efecto en mock
    return null;
  },

  // ── CMS ────────────────────────────────────────────────────────────
  create: async (data) => {
    if (!USE_MOCK) {
      // Traducimos payload del frontend a la forma que espera el backend.
      const payload = {
        categoriaId:      data.categoryId,
        slug:             data.slug,
        titulo:           data.title,
        subtitulo:        data.subtitle,
        resumen:          data.excerpt,
        cuerpo:           data.content,
        formatoCuerpo:    data.contentFormat || 'markdown',
        tipo:             data.type || 'nota',
        heroImagenUrl:    data.imageUrl ?? data.image,
        heroImagenAlt:    data.imageAlt ?? data.imageCaption,
        heroImagenCredito: data.imageCredit,
        metaTitulo:       data.metaTitle,
        metaDescripcion:  data.metaDescription,
        ogImagenUrl:      data.ogImage,
        destacado:        !!data.featured,
        esUltimaHora:     !!data.breaking || !!data.isBreaking,
        tiempoLecturaMin: data.readingTime,
        autorIds:         data.authorIds || (data.authorId ? [data.authorId] : []),
        tagIds:           data.tagIds || [],
      };
      const a = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos`,
        { method: 'POST', body: JSON.stringify(payload) });
      return mapArticleDetail(a);
    }
    await delay(200);
    const newArticle = {
      id: articles.length + 1, ...data,
      slug: data.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      views: 0, publishedAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    articles.push(newArticle);
    return newArticle;
  },

  update: async (id, data) => {
    if (!USE_MOCK) {
      const payload = {
        categoriaId:      data.categoryId,
        slug:             data.slug,
        titulo:           data.title,
        subtitulo:        data.subtitle,
        resumen:          data.excerpt,
        cuerpo:           data.content,
        formatoCuerpo:    data.contentFormat || 'markdown',
        tipo:             data.type || 'nota',
        heroImagenUrl:    data.imageUrl ?? data.image,
        heroImagenAlt:    data.imageAlt ?? data.imageCaption,
        heroImagenCredito: data.imageCredit,
        metaTitulo:       data.metaTitle,
        metaDescripcion:  data.metaDescription,
        ogImagenUrl:      data.ogImage,
        destacado:        !!data.featured,
        esUltimaHora:     !!data.breaking || !!data.isBreaking,
        tiempoLecturaMin: data.readingTime,
        autorIds:         data.authorIds || (data.authorId ? [data.authorId] : []),
        tagIds:           data.tagIds || [],
      };
      const a = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos/${id}`,
        { method: 'PUT', body: JSON.stringify(payload) });
      return mapArticleDetail(a);
    }
    await delay(200);
    const index = articles.findIndex(a => a.id === parseInt(id));
    if (index === -1) throw new Error('Artículo no encontrado');
    articles[index] = { ...articles[index], ...data, updatedAt: new Date().toISOString() };
    return articles[index];
  },

  changeStatus: async (id, status, publishedAt = null) => {
    if (!USE_MOCK) {
      // Traducción de estados: front usa 'draft'/'published'/'archived';
      // backend usa 'borrador'/'publicado'/'archivado' (+ 'en_revision').
      const estadoMap = { draft: 'borrador', published: 'publicado', archived: 'archivado', review: 'en_revision' };
      const a = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos/${id}/estado`,
        { method: 'PATCH', body: JSON.stringify({ estado: estadoMap[status] || status, publicadoEn: publishedAt }) });
      return mapArticleDetail(a);
    }
    return null;
  },

  delete: async (id) => {
    if (!USE_MOCK) {
      await fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos/${id}`, { method: 'DELETE' });
      return { success: true };
    }
    await delay(200);
    const index = articles.findIndex(a => a.id === parseInt(id));
    if (index === -1) throw new Error('Artículo no encontrado');
    articles.splice(index, 1);
    return { success: true };
  },

  getRelated: async (articleId, limit = 4) => {
    if (!USE_MOCK) {
      // El backend .NET no tiene endpoint dedicado — dejamos vacío por
      // ahora. Se puede implementar más adelante como
      // "top N de la misma categoría publicados recientemente".
      return [];
    }
    await delay(100);
    const article = articles.find(a => a.id === parseInt(articleId));
    if (!article) return [];
    return articles
      .filter(a => a.id !== parseInt(articleId) && a.categoryId === article.categoryId)
      .slice(0, limit);
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  CATEGORÍAS
// ═══════════════════════════════════════════════════════════════════════
export const categoryService = {
  getAll: async () => {
    if (!USE_MOCK) {
      const list = await fetchApi(`/periodicos/${PERIODICO_ID}/categorias`);
      return list.map(mapCategoria);
    }
    await delay(50);
    return categories;
  },

  getById: async (id) => {
    if (!USE_MOCK) {
      // El backend expone por slug — para búsqueda por id el frontend
      // trae todas y filtra. Aceptable dado el tamaño típico (<50 cat).
      const list = await fetchApi(`/periodicos/${PERIODICO_ID}/categorias`);
      return mapCategoria(list.find(c => c.id === id));
    }
    await delay(50);
    return categories.find(c => c.id === id);
  },

  getBySlug: async (slug) => {
    if (!USE_MOCK) {
      const c = await fetchApi(`/periodicos/${PERIODICO_ID}/categorias/slug/${encodeURIComponent(slug)}`);
      return mapCategoria(c);
    }
    await delay(50);
    const category = categories.find(c => c.slug === slug);
    if (!category) throw new Error('Categoría no encontrada');
    return category;
  },

  create: async (data) => {
    if (!USE_MOCK) {
      const payload = {
        slug: data.slug, nombre: data.name, descripcion: data.description,
        color: data.color, icono: data.icon, orden: data.order || 0, parentId: data.parentId,
      };
      const c = await fetchApi(`/periodicos/${PERIODICO_ID}/categorias`,
        { method: 'POST', body: JSON.stringify(payload) });
      return mapCategoria(c);
    }
    await delay(200);
    const newCategory = {
      id: categories.length + 1, ...data,
      slug: data.name.toLowerCase().replace(/\s+/g, '-'),
    };
    categories.push(newCategory);
    return newCategory;
  },

  update: async (id, data) => {
    if (!USE_MOCK) {
      const payload = {
        slug: data.slug, nombre: data.name, descripcion: data.description,
        color: data.color, icono: data.icon, orden: data.order || 0, parentId: data.parentId,
        activa: data.active !== false,
      };
      const c = await fetchApi(`/periodicos/${PERIODICO_ID}/categorias/${id}`,
        { method: 'PUT', body: JSON.stringify(payload) });
      return mapCategoria(c);
    }
    await delay(200);
    const index = categories.findIndex(c => c.id === parseInt(id));
    if (index === -1) throw new Error('Categoría no encontrada');
    categories[index] = { ...categories[index], ...data };
    return categories[index];
  },

  delete: async (id) => {
    if (!USE_MOCK) {
      await fetchApi(`/periodicos/${PERIODICO_ID}/categorias/${id}`, { method: 'DELETE' });
      return { success: true };
    }
    await delay(200);
    const index = categories.findIndex(c => c.id === parseInt(id));
    if (index === -1) throw new Error('Categoría no encontrada');
    categories.splice(index, 1);
    return { success: true };
  },

  /** items: [{ id, orden }, ...] — actualiza el orden de varias categorías en una transacción. */
  reorder: async (items) => {
    if (!USE_MOCK) {
      return fetchApi(`/periodicos/${PERIODICO_ID}/categorias/reordenar`, {
        method: 'POST',
        body: JSON.stringify(items),
      });
    }
    await delay(50);
    return { actualizadas: items.length };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  AUTORES
// ═══════════════════════════════════════════════════════════════════════
export const authorService = {
  getAll: async () => {
    if (!USE_MOCK) {
      const list = await fetchApi(`/periodicos/${PERIODICO_ID}/autores`);
      return list.map(mapAutor);
    }
    await delay(50);
    return authors;
  },

  getById: async (id) => {
    if (!USE_MOCK) {
      const list = await fetchApi(`/periodicos/${PERIODICO_ID}/autores`);
      return mapAutor(list.find(a => a.id === id));
    }
    await delay(50);
    const author = authors.find(a => a.id === parseInt(id));
    if (!author) throw new Error('Autor no encontrado');
    return author;
  },

  getBySlug: async (slug) => {
    if (!USE_MOCK) {
      const a = await fetchApi(`/periodicos/${PERIODICO_ID}/autores/slug/${encodeURIComponent(slug)}`);
      return mapAutor(a);
    }
    await delay(50);
    const author = authors.find(a => a.slug === slug);
    if (!author) throw new Error('Autor no encontrado');
    return author;
  },

  getArticles: async (authorId, opts = {}) => {
    // Acepta un número (legacy: solo limit) o un objeto { page, limit }.
    const limit = typeof opts === 'number' ? opts : (opts.limit || 20);
    const page  = typeof opts === 'number' ? 1     : (opts.page  || 1);
    if (!USE_MOCK) {
      // El backend .NET no expone artículos por autor directamente,
      // así que traemos un batch grande y filtramos client-side.
      // Para volúmenes grandes habría que agregar endpoint dedicado.
      const paged = await fetchApi(`/periodicos/${PERIODICO_ID}/articulos?tamano=100`);
      const mapped = (paged.items || []).map(mapArticleList);
      const soloDelAutor = mapped.filter(a =>
        a.authors?.some(x => x.id === authorId)
      );
      // Paginación client-side sobre el resultado filtrado.
      const start = (page - 1) * limit;
      return soloDelAutor.slice(start, start + limit);
    }
    await delay(100);
    return articles.filter(a => a.authorId === parseInt(authorId));
  },

  create: async (data) => {
    if (!USE_MOCK) {
      const payload = {
        slug: data.slug, nombre: data.name, bio: data.bio,
        avatarUrl: data.avatar, cargo: data.role, emailPublico: data.email,
        celular: data.celular,
        twitter: data.twitter, instagram: data.instagram,
        linkedin: data.linkedin, sitioWeb: data.website,
        usuarioId: data.userId,
      };
      const a = await fetchApi(`/periodicos/${PERIODICO_ID}/autores`,
        { method: 'POST', body: JSON.stringify(payload) });
      return mapAutor(a);
    }
    await delay(200);
    const newAuthor = { id: authors.length + 1, ...data };
    authors.push(newAuthor);
    return newAuthor;
  },

  update: async (id, data) => {
    if (!USE_MOCK) {
      const payload = {
        slug: data.slug, nombre: data.name, bio: data.bio,
        avatarUrl: data.avatar, cargo: data.role, emailPublico: data.email,
        celular: data.celular,
        twitter: data.twitter, instagram: data.instagram,
        linkedin: data.linkedin, sitioWeb: data.website,
        usuarioId: data.userId,
        activo: data.active !== false,
      };
      const a = await fetchApi(`/periodicos/${PERIODICO_ID}/autores/${id}`,
        { method: 'PUT', body: JSON.stringify(payload) });
      return mapAutor(a);
    }
    return null;
  },

  delete: async (id) => {
    if (!USE_MOCK) {
      await fetchApi(`/periodicos/${PERIODICO_ID}/autores/${id}`, { method: 'DELETE' });
      return { success: true };
    }
    return { success: true };
  },

  // ── Puente autor ↔ usuario del sistema ────────────────────────────
  /** Vincular un autor existente a un usuario del ecosistema. */
  linkUser: async (autorId, usuarioId) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/periodicos/${PERIODICO_ID}/autores/${autorId}/vincular-usuario`, {
      method: 'POST',
      body: JSON.stringify({ usuarioId }),
    });
  },

  /** Romper el vínculo autor↔usuario. El autor queda standalone. */
  unlinkUser: async (autorId) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/periodicos/${PERIODICO_ID}/autores/${autorId}/desvincular-usuario`, {
      method: 'POST',
    });
  },

  /** Crear un autor prellenando datos desde un usuario_cache. */
  createFromUser: async (usuarioId) => {
    if (USE_MOCK) return null;
    return fetchApi(`/periodicos/${PERIODICO_ID}/autores/desde-usuario/${usuarioId}`, {
      method: 'POST',
    });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  EDICIONES
// ═══════════════════════════════════════════════════════════════════════
// El backend devuelve el ArticuloListDto embebido dentro de cada
// EdicionArticuloDto, así que un solo GET trae la edición completa
// con todos sus artículos listos para renderizar (sin N+1).
// ═══════════════════════════════════════════════════════════════════════
export const editionService = {
  getAll: async (params = {}) => {
    if (!USE_MOCK) {
      const limite = params.limit || 24;
      // Si params.status === 'draft' → uso el listado admin (todas).
      // Si no, uso el público (solo publicadas).
      const isAdmin = params.status && params.status !== 'published';
      const path = isAdmin
        ? `/admin/periodicos/${PERIODICO_ID}/ediciones?limite=${limite}`
        : `/periodicos/${PERIODICO_ID}/ediciones?limite=${limite}`;
      const list = await fetchApi(path);
      let mapped = list.map(mapEdition);
      if (params.status) mapped = mapped.filter(e => e.status === params.status);
      return mapped;
    }
    await delay(100);
    let result = [...editions];
    if (params.status) result = result.filter(e => e.status === params.status);
    result.sort((a, b) => new Date(b.date) - new Date(a.date));
    return result;
  },

  // Listado admin: incluye borradores y archivadas. Usa el endpoint
  // /admin que requiere JWT. Reservado para el panel administrativo.
  getAllAdmin: async (params = {}) => {
    if (!USE_MOCK) {
      const limite = params.limit || 100;
      const list = await fetchApi(
        `/admin/periodicos/${PERIODICO_ID}/ediciones?limite=${limite}`);
      return list.map(mapEdition);
    }
    return editionService.getAll(params);
  },

  getById: async (id) => {
    if (!USE_MOCK) {
      // Detalle vía admin (requiere JWT). Se usa desde el CMS.
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${id}`);
      return mapEdition(e);
    }
    await delay(50);
    const edition = editions.find(e => e.id === parseInt(id));
    if (!edition) throw new Error('Edición no encontrada');
    const editionArticles = edition.articles.map(articleId => {
      const article = articles.find(a => a.id === articleId);
      return article ? {
        ...article,
        category: categories.find(c => c.id === article.categoryId),
        author:   authors.find(a => a.id === article.authorId),
      } : null;
    }).filter(Boolean);
    return { ...edition, articles: editionArticles };
  },

  /** Detalle público por id — sin JWT, solo devuelve publicadas.
   *  Se usa desde la vista de revista (/edicion/{id}/revista). */
  getPublicById: async (id) => {
    if (!USE_MOCK) {
      const e = await fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${id}`);
      return mapEdition(e);
    }
    return editionService.getById(id);
  },

  getByNumber: async (number) => {
    if (!USE_MOCK) {
      const e = await fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/numero/${number}`);
      return mapEdition(e);
    }
    await delay(50);
    const edition = editions.find(e => e.number === parseInt(number));
    if (!edition) throw new Error('Edición no encontrada');
    const editionArticles = edition.articles.map(articleId => {
      const article = articles.find(a => a.id === articleId);
      return article ? {
        ...article,
        category: categories.find(c => c.id === article.categoryId),
        author:   authors.find(a => a.id === article.authorId),
      } : null;
    }).filter(Boolean);
    return { ...edition, articles: editionArticles };
  },

  getLatest: async ({ departmentId } = {}) => {
    if (!USE_MOCK) {
      // Nuevo endpoint dedicado con fallback regional → global en el
      // backend. Devuelve 204 si no hay ninguna publicada (poco
      // probable si hay al menos una global).
      const qs = departmentId != null ? `?departamentoId=${departmentId}` : '';
      const e = await fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/latest${qs}`);
      return e ? mapEdition(e) : null;
    }
    await delay(50);
    const published = editions.filter(e => e.status === 'published');
    return published.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  },

  create: async (data) => {
    if (!USE_MOCK) {
      const payload = {
        numero:       data.number,
        fecha:        data.date,  // ISO date
        titulo:       data.title,
        pdfUrl:       data.pdfUrl,
        portadaUrl:   data.coverUrl,
        esPorDefecto: !!data.isDefault,
        totalPaginas: data.totalPages ?? 16,
        // NULL = edición global. Un ID de municipio: el backend
        // resuelve depto, país y coordenadas contra Ecolab y guarda
        // el snapshot completo. isDefault=true fuerza NULL.
        municipioId:  data.isDefault ? null : (data.municipalityId ?? null),
        articulos:  (data.articles || []).map((art, idx) => ({
          articuloId: art.articleId || art.id,
          pagina:     art.page,
          orden:      art.order ?? idx,
          esPortada:  !!art.isCover,
        })),
      };
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones`,
        { method: 'POST', body: JSON.stringify(payload) });
      return mapEdition(e);
    }
    await delay(200);
    const lastEdition = editions.sort((a, b) => b.number - a.number)[0];
    const newEdition = {
      id: editions.length + 1, number: lastEdition.number + 1, ...data,
      status: 'draft', articles: [], pages: [],
      createdAt: new Date().toISOString(), publishedAt: null,
    };
    editions.push(newEdition);
    return newEdition;
  },

  update: async (id, data) => {
    if (!USE_MOCK) {
      const payload = {
        numero:       data.number,
        fecha:        data.date,
        titulo:       data.title,
        pdfUrl:       data.pdfUrl,
        portadaUrl:   data.coverUrl,
        esPorDefecto: !!data.isDefault,
        totalPaginas: data.totalPages ?? 16,
        municipioId:  data.isDefault ? null : (data.municipalityId ?? null),
        articulos:  (data.articles || []).map((art, idx) => ({
          articuloId: art.articleId || art.id,
          pagina:     art.page,
          orden:      art.order ?? idx,
          esPortada:  !!art.isCover,
        })),
      };
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${id}`,
        { method: 'PUT', body: JSON.stringify(payload) });
      return mapEdition(e);
    }
    await delay(200);
    const index = editions.findIndex(e => e.id === parseInt(id));
    if (index === -1) throw new Error('Edición no encontrada');
    editions[index] = { ...editions[index], ...data };
    return editions[index];
  },

  publish: async (id) => {
    if (!USE_MOCK) {
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${id}/publicar`, { method: 'POST' });
      return mapEdition(e);
    }
    await delay(300);
    const index = editions.findIndex(e => e.id === parseInt(id));
    if (index === -1) throw new Error('Edición no encontrada');
    editions[index] = { ...editions[index], status: 'published', publishedAt: new Date().toISOString() };
    return editions[index];
  },

  addArticle: async (editionId, articleId) => {
    if (!USE_MOCK) {
      // El backend .NET recibe la lista completa de artículos en un
      // update. Traemos la edición, agregamos y regrabamos.
      // Shape actual del GET: [{ pagina, orden, articulo: { id, ... } }].
      // Shape que espera el PUT: [{ articuloId, pagina, orden }].
      const current = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${editionId}`);
      const existing = (current.articulos || []).map(x => ({
        articuloId: x.articulo?.id ?? x.articuloId,
        pagina:     x.pagina,
        orden:      x.orden,
      }));
      if (!existing.some(x => x.articuloId === articleId)) {
        existing.push({ articuloId: articleId, pagina: null, orden: existing.length });
      }
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${editionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          numero: current.numero, fecha: current.fecha, titulo: current.titulo,
          pdfUrl: current.pdfUrl, portadaUrl: current.portadaUrl,
          articulos: existing,
        }),
      });
      return mapEdition(e);
    }
    await delay(100);
    const edition = editions.find(e => e.id === parseInt(editionId));
    if (!edition) throw new Error('Edición no encontrada');
    if (!edition.articles.includes(parseInt(articleId))) edition.articles.push(parseInt(articleId));
    return edition;
  },

  removeArticle: async (editionId, articleId) => {
    if (!USE_MOCK) {
      const current = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${editionId}`);
      const remaining = (current.articulos || [])
        .map(x => ({
          articuloId: x.articulo?.id ?? x.articuloId,
          pagina:     x.pagina,
          orden:      x.orden,
        }))
        .filter(x => x.articuloId !== articleId);
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${editionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          numero: current.numero, fecha: current.fecha, titulo: current.titulo,
          pdfUrl: current.pdfUrl, portadaUrl: current.portadaUrl,
          articulos: remaining,
        }),
      });
      return mapEdition(e);
    }
    await delay(100);
    const edition = editions.find(e => e.id === parseInt(editionId));
    if (!edition) throw new Error('Edición no encontrada');
    edition.articles = edition.articles.filter(id => id !== parseInt(articleId));
    return edition;
  },

  generatePDF: async (id) => {
    if (!USE_MOCK) {
      // El backend no genera PDFs — sirve el que ya subieron. Devolvemos
      // la URL guardada en pdfUrl.
      const e = await fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/${id}`);
      return { url: gsToHttps(e.pdfUrl) };
    }
    await delay(500);
    return { url: `/editions/${id}/download` };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  PERIÓDICO / CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════
export const configService = {
  get: async () => {
    if (!USE_MOCK) {
      // Los datos de "configuración del periódico" (nombre, dominio,
      // logo, colores) viven en /periodicos/{id}. Los devolvemos con la
      // forma que espera el frontend.
      const p = await fetchApi(`/periodicos/${PERIODICO_ID}`);
      const m = mapPeriodico(p);
      return {
        ...newspaperConfig, // defaults del mock (branding fallback)
        name:        m.name,
        description: m.description,
        domain:      m.domain,
        logo:        m.logo,
        colors:      { primary: m.colorPrimary, secondary: m.colorSecondary },
      };
    }
    await delay(50);
    return newspaperConfig;
  },

  update: async (data) => {
    if (!USE_MOCK) {
      const payload = {
        nombre:          data.name,
        dominio:         data.domain,
        descripcion:     data.description,
        logoUrl:         data.logo,
        isotipoUrl:      data.isotype,
        colorPrimario:   data.colors?.primary,
        colorSecundario: data.colors?.secondary,
        ciudadId:        data.cityId,
        activo:          data.active !== false,
      };
      const p = await fetchApi(`/periodicos/${PERIODICO_ID}`,
        { method: 'PUT', body: JSON.stringify(payload) });
      return mapPeriodico(p);
    }
    await delay(200);
    Object.assign(newspaperConfig, data);
    return newspaperConfig;
  },

  // ── Parámetros editoriales / operativos (Fase A+B+C) ─────────────
  // Backing: news_main.periodicos_config (script 011). El backend
  // crea la fila con defaults la primera vez que se pide.
  getEditorial: async () => {
    if (USE_MOCK) return null;
    return fetchApi(`/admin/periodicos/${PERIODICO_ID}/config`);
  },

  updateEditorial: async (payload) => {
    if (USE_MOCK) return payload;
    return fetchApi(`/admin/periodicos/${PERIODICO_ID}/config`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /** Sugiere el próximo número de edición según formato configurado. */
  sugerirNumeroEdicion: async () => {
    if (USE_MOCK) return { numero: '1' };
    return fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones/sugerir-numero`);
  },

  /**
   * Endpoint PÚBLICO — snapshot del Director del periódico para el
   * footer y la sección Nosotros. El backend devuelve null si no hay
   * Director configurado (no es error, es estado válido).
   */
  getDirectorPublico: async () => {
    if (USE_MOCK) return null;
    return fetchApi(`/periodicos/${PERIODICO_ID}/director`);
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  PUBLICIDAD  (sin equivalente en el backend .NET actualmente)
// ═══════════════════════════════════════════════════════════════════════
export const advertisingService = {
  getByPosition: async (position) => {
    // Backend .NET no expone publicidad todavía; devolvemos vacío para
    // que la landing/home no rompa y usamos mock cuando aplique.
    if (!USE_MOCK) return [];
    await delay(50);
    return advertising.filter(a => a.position === position && a.active);
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  BÚSQUEDA
// ═══════════════════════════════════════════════════════════════════════
export const searchService = {
  search: async (query, options = {}) => {
    if (!USE_MOCK) {
      // El backend hace la búsqueda dentro del listado de artículos.
      // Para categorías y autores hacemos filter client-side sobre
      // los listados públicos (volúmenes pequeños).
      const [paged, cats, auts] = await Promise.all([
        fetchApi(`/periodicos/${PERIODICO_ID}/articulos?q=${encodeURIComponent(query)}&tamano=${options.limit || 10}`),
        fetchApi(`/periodicos/${PERIODICO_ID}/categorias`),
        fetchApi(`/periodicos/${PERIODICO_ID}/autores`),
      ]);
      const q = query.toLowerCase();
      return {
        articles:   (paged.items || []).map(mapArticleList),
        categories: cats.map(mapCategoria).filter(c => c.name.toLowerCase().includes(q)),
        authors:    auts.map(mapAutor).filter(a => a.name.toLowerCase().includes(q)),
      };
    }
    await delay(150);
    const searchLower = query.toLowerCase();
    return {
      articles: articles.filter(a =>
        a.title.toLowerCase().includes(searchLower) ||
        a.excerpt.toLowerCase().includes(searchLower) ||
        a.content.toLowerCase().includes(searchLower) ||
        a.tags.some(t => t.toLowerCase().includes(searchLower))
      ).slice(0, options.limit || 10),
      categories: categories.filter(c => c.name.toLowerCase().includes(searchLower)),
      authors:    authors.filter(a => a.name.toLowerCase().includes(searchLower)),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  ESTADÍSTICAS
// ═══════════════════════════════════════════════════════════════════════
export const statsService = {
  getOverview: async () => {
    if (!USE_MOCK) {
      // El backend no tiene un endpoint dedicado de stats. Componemos
      // el overview con múltiples llamadas ligeras. Cuando el volumen
      // crezca conviene un endpoint /admin/stats/overview dedicado.
      const [pagedPub, pagedBorr, cats, eds] = await Promise.all([
        fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos?estado=publicado&tamano=100`),
        fetchApi(`/admin/periodicos/${PERIODICO_ID}/articulos?estado=borrador&tamano=100`),
        fetchApi(`/periodicos/${PERIODICO_ID}/categorias`),
        fetchApi(`/admin/periodicos/${PERIODICO_ID}/ediciones?limite=100`),
      ]);
      const publicados = (pagedPub.items || []).map(mapArticleList);
      const borradores = (pagedBorr.items || []).map(mapArticleList);
      const totalArticles = pagedPub.total + pagedBorr.total;
      return {
        totalArticles,
        totalDrafts:       pagedBorr.total,
        totalEditions:     eds.length,
        publishedEditions: eds.filter(e => e.estado === 'publicada').length,
        totalViews:        publicados.reduce((sum, a) => sum + (a.views || 0), 0),
        topCategories:     cats.map(mapCategoria).slice(0, 5),
        topArticles:       [...publicados].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5),
      };
    }
    await delay(100);
    return {
      totalArticles:     articles.length,
      totalEditions:     editions.length,
      totalViews:        articles.reduce((sum, a) => sum + a.views, 0),
      publishedEditions: editions.filter(e => e.status === 'published').length,
      topCategories:     categories.map(c => ({
        ...c,
        articleCount: articles.filter(a => a.categoryId === c.id).length,
      })).sort((a, b) => b.articleCount - a.articleCount).slice(0, 5),
      topArticles:       [...articles].sort((a, b) => b.views - a.views).slice(0, 5),
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  AUTENTICACIÓN
// ═══════════════════════════════════════════════════════════════════════
// El login real (email/password contra Ecolab) vive en AuthContext.
// Aquí exponemos wrappers de compatibilidad y helpers que dependen del
// backend .NET ya autenticado (perfil, afiliaciones).
// ═══════════════════════════════════════════════════════════════════════
export const authService = {
  /**
   * @deprecated Usar useAuth().login(email, password) desde componentes.
   * Wrapper para compat con código legacy: replica el flujo del contexto
   * llamando al mismo endpoint de Ecolab. Guarda el token para que las
   * siguientes llamadas de este mismo módulo lo adjunten automáticamente.
   */
  login: async (email, password) => {
    if (USE_MOCK) {
      await delay(200);
      if (email === 'admin@periodico.com' && password === 'admin123') {
        const token = 'mock-jwt-token';
        localStorage.setItem('newspaperToken', token);
        return {
          success: true, token,
          user: { id: 1, name: 'Administrador', email, role: 'admin' },
        };
      }
      throw new Error('Credenciales inválidas');
    }

    const ecolabUrl = process.env.REACT_APP_ECOLAB_API_URL ||
      'https://ecolab-backend-723916953839.us-central1.run.app';
    const resp = await fetch(`${ecolabUrl}/api/ecolab/auth/login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Error ${resp.status}`);
    }
    const data = await resp.json();
    const accessToken = data.accessToken || data.access_token || data.token || data.jwt;
    if (accessToken) localStorage.setItem('newspaperToken', accessToken);
    return { success: true, token: accessToken, user: data.usuario || data.user };
  },

  logout: () => localStorage.removeItem('newspaperToken'),

  getProfile: async () => {
    if (!USE_MOCK) {
      const u = await fetchApi('/me');
      return {
        id:     u.id,
        name:   [u.nombre, u.apellido].filter(Boolean).join(' ') || u.email,
        email:  u.email,
        avatar: gsToHttps(u.avatarUrl),
        role:   'staff',  // el rol real depende del periódico, ver getAffiliations()
      };
    }
    await delay(50);
    return { id: 1, name: 'Administrador', email: 'admin@periodico.com', role: 'admin' };
  },

  /**
   * Periódicos a los que el usuario logueado está afiliado y con qué rol.
   * Retorna array de { periodicoId, slug, name, logo, role }.
   */
  getAffiliations: async () => {
    if (!USE_MOCK) {
      const list = await fetchApi('/me/afiliaciones');
      return list.map(a => ({
        periodicoId: a.periodicoId,
        slug:        a.slug,
        name:        a.nombre,
        logo:        gsToHttps(a.logoUrl),
        role:        a.rol,
      }));
    }
    return [{ periodicoId: PERIODICO_ID, slug: 'mock', name: 'Mock', logo: null, role: 'director' }];
  },

  /**
   * Roles INTERNOS de News del usuario logueado.
   * Devuelve array de strings: ['Editor'], ['Redactor','Colaborador'], o [] si
   * el usuario aún no tiene rol asignado en News (pero sí acceso a la app
   * por Ecolab). El frontend usa esto para gatear el panel /admin.
   */
  getNewsRoles: async () => {
    if (USE_MOCK) return ['Editor'];
    try {
      const roles = await fetchApi('/me/roles');
      return Array.isArray(roles) ? roles : [];
    } catch (err) {
      console.warn('No se pudieron cargar roles de News:', err.message);
      return [];
    }
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  STORAGE (uploads al bucket de Firebase Storage de News)
// ═══════════════════════════════════════════════════════════════════════
export const storageService = {
  /** Sube una pieza multimedia (MP3 de entrevista o MP4 de reportaje) al bucket
   *  del periódico. El servidor valida formato, peso y faststart según
   *  MULTIMEDIA.md; si rechaza, el mensaje trae el comando de ffmpeg. */
  subirMultimedia: async (edicionId, archivo, tipo = 'audio') => {
    const fd = new FormData(); fd.append('archivo', archivo);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(
      `${API_BASE_URL}/storage/multimedia?edicionId=${edicionId}&tipo=${tipo}`,
      { method: 'POST', headers: token ? { 'Authorization': `Bearer ${token}` } : {}, body: fd }
    );
    if (!resp.ok) {
      let detalle = `HTTP ${resp.status}`;
      try { const e = await resp.json(); detalle = e.error || e.title || detalle; } catch {}
      throw new Error(detalle);
    }
    return await resp.json();
  },

  // Listar objetos del bucket (modo repositorio físico)
  listarBucket: async (prefijo = '') => fetchApi(`/storage/listar?prefijo=${encodeURIComponent(prefijo)}`),

  // Upload genérico — para imágenes sin entidad específica (inline en artículos, etc.)
  uploadImagen: async (file, carpeta = 'general') => {
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/imagen?carpeta=${carpeta}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.title || `Upload falló: ${resp.status}`); }
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  uploadArticleHero: async (articleId, file) => {
    if (USE_MOCK) return { gsUri: 'gs://mock', url: URL.createObjectURL(file) };
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/articulos/${articleId}/hero`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) throw new Error(`Upload falló: ${resp.status}`);
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  uploadArticleImage: async (articleId, file) => {
    if (USE_MOCK) return { gsUri: 'gs://mock', url: URL.createObjectURL(file) };
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/articulos/${articleId}/imagen`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) throw new Error(`Upload falló: ${resp.status}`);
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  uploadAuthorAvatar: async (authorId, file) => {
    if (USE_MOCK) return { gsUri: 'gs://mock', url: URL.createObjectURL(file) };
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/autores/${authorId}/avatar`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) throw new Error(`Upload falló: ${resp.status}`);
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  uploadMaterialPublicidad: async (pautaId, file, tipo = 'imagen') => {
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const endpoint = tipo === 'video' ? 'video' : tipo === 'audio' ? 'audio' : 'imagen';
    const resp = await fetch(`${API_BASE_URL}/storage/publicidad/${pautaId}/${endpoint}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.title || err.message || `Upload falló: ${resp.status}`);
    }
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  uploadEditionPdf: async (editionId, file) => {
    if (USE_MOCK) return { gsUri: 'gs://mock', url: URL.createObjectURL(file) };
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/ediciones/${editionId}/pdf`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) throw new Error(`Upload falló: ${resp.status}`);
    const data = await resp.json();
    return { gsUri: data.gsUri, url: gsToHttps(data.gsUri), signedUrl: data.signedUrl };
  },

  getSignedUrl: async (gsUri, validHours = 1) => {
    if (USE_MOCK || !gsUri) return null;
    const data = await fetchApi(`/storage/signed-url?gsUri=${encodeURIComponent(gsUri)}&validHours=${validHours}`);
    return data?.signedUrl;
  },

  delete: async (gsUri) => {
    if (USE_MOCK) return { success: true };
    await fetchApi(`/storage?gsUri=${encodeURIComponent(gsUri)}`, { method: 'DELETE' });
    return { success: true };
  },

  /** Sube imagen a un bloque de tipo elemento_fijo y persiste ElementoData en el backend.
   *  Devuelve { gsUri, signedUrl, elementoData }.
   *  objectFit: "contain" (default) | "fill"
   */
  subirImagenElementoFijo: async (bloqueId, file, objectFit = 'contain') => {
    if (USE_MOCK) return { gsUri: 'gs://mock', signedUrl: URL.createObjectURL(file), elementoData: '{}' };
    const fd = new FormData(); fd.append('archivo', file);
    const token = localStorage.getItem('newspaperToken');
    const resp = await fetch(`${API_BASE_URL}/storage/maqueta/bloques/${bloqueId}/imagen?objectFit=${objectFit}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!resp.ok) throw new Error(`Upload falló: ${resp.status}`);
    const data = await resp.json();
    // Si el backend no devuelve signedUrl (revisión anterior), pedirla por separado
    const signedUrl = data.signedUrl
      || await storageService.getSignedUrl(data.gsUri, 24);
    return { gsUri: data.gsUri, signedUrl, elementoData: data.elementoData };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  USUARIOS Y ROLES (panel administrativo)
//  Ver ECOSISTEMA_IDENTIDAD_Y_ROLES.md §5 y §6.
//  Todos los endpoints requieren JWT con rol de News suficiente
//  (típicamente Editor). El backend valida escalera.
// ═══════════════════════════════════════════════════════════════════════
export const userAdminService = {
  /**
   * Listado paginado de usuarios con al menos un rol activo en News.
   * @param {{ search?: string, limit?: number }} params
   * @returns {Promise<Array<{ id, ecolabUserId, nombre, apellido, email, avatarUrl, activo, ultimoLogin, roles: Array<{ rol, asignadoEn, asignadoPorNombre }> }>>}
   */
  list: async ({ search = '', limit = 50 } = {}) => {
    if (USE_MOCK) return [];
    const qs = new URLSearchParams();
    if (search) qs.set('q', search);
    if (limit)  qs.set('limite', String(limit));
    return fetchApi(`/admin/usuarios?${qs.toString()}`);
  },

  /** Detalle de un usuario con sus roles activos. */
  getById: async (id) => {
    if (USE_MOCK) return null;
    return fetchApi(`/admin/usuarios/${id}`);
  },

  /** Historial de asignaciones/revocaciones (últimas N). */
  getHistory: async (id, limit = 20) => {
    if (USE_MOCK) return [];
    return fetchApi(`/admin/usuarios/${id}/historial?limite=${limit}`);
  },

  /** Asigna un rol al usuario. Backend valida escalera. */
  assignRole: async (id, rol) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/admin/usuarios/${id}/roles`, {
      method: 'POST',
      body: JSON.stringify({ rol }),
    });
  },

  /** Revoca un rol activo (soft-delete). */
  revokeRole: async (id, rol) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/admin/usuarios/${id}/roles/${rol}`, {
      method: 'DELETE',
    });
  },

  // ── Scope regional: departamentos asignados al usuario ─────────
  // Ver 013_editor_nacional_y_regiones.sql. Aplica a Editor,
  // Redactor y Colaborador. EditorNacional ignora esta tabla.

  /** Lista los departamentos asignados a un usuario. */
  listRegions: async (id) => {
    if (USE_MOCK) return [];
    return fetchApi(`/admin/usuarios/${id}/regiones`);
  },

  /** Asigna un departamento al usuario. */
  assignRegion: async (id, departmentId, departmentName) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/admin/usuarios/${id}/regiones`, {
      method: 'POST',
      body: JSON.stringify({
        departamentoId: departmentId,
        departamentoNombre: departmentName ?? null,
      }),
    });
  },

  /** Quita un departamento del usuario. */
  revokeRegion: async (id, departmentId) => {
    if (USE_MOCK) return { success: true };
    return fetchApi(`/admin/usuarios/${id}/regiones/${departmentId}`, {
      method: 'DELETE',
    });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  GEOGRAFÍA (catálogo público)
// ═══════════════════════════════════════════════════════════════════════
export const geographyService = {
  getCountries: async () => {
    if (USE_MOCK) return [];
    return fetchApi('/geografia/paises');
  },
  getDepartments: async (countryId) => {
    if (USE_MOCK) return [];
    return fetchApi(`/geografia/paises/${countryId}/departamentos`);
  },
  /**
   * Todos los departamentos, sin filtrar por país. Se usa para el
   * selector manual de región del header.
   */
  getAllDepartments: async () => {
    if (USE_MOCK) return [];
    return fetchApi('/geografia/departamentos');
  },
  getCities: async (departmentId) => {
    if (USE_MOCK) return [];
    return fetchApi(`/geografia/departamentos/${departmentId}/municipios`);
  },
};

export default {
  articles:    articleService,
  categories:  categoryService,
  authors:     authorService,
  editions:    editionService,
  config:      configService,
  advertising: advertisingService,
  search:      searchService,
  stats:       statsService,
  auth:        authService,
  storage:     storageService,
  geography:   geographyService,
  usersAdmin:  userAdminService,
};

// Exportado para que AppContext resuelva el tenant al arrancar la app
// (antes de disparar los primeros loads paralelos).
export { resolvePeriodicoId };

// ─── PUBLICIDAD ──────────────────────────────────────────────────────────────

const _getPeriodicoId = async () => {
  if (PERIODICO_ID) return PERIODICO_ID;
  const p = await fetchApi('/periodicos/default');
  PERIODICO_ID = p.id;
  return PERIODICO_ID;
};
const pubBase = async () => `/periodicos/${await _getPeriodicoId()}/publicidad`;
const pubCfg  = async () => `${await pubBase()}/config`;

export const publicidadConfigService = {
  // Sistemas de medición
  getSistemas:          async ()       => fetchApi(`${await pubCfg()}/sistemas-medicion`),
  crearSistema:         async (dto)    => fetchApi(`${await pubCfg()}/sistemas-medicion`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarSistema:    async (id,dto) => fetchApi(`${await pubCfg()}/sistemas-medicion/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarSistema:      async (id)     => fetchApi(`${await pubCfg()}/sistemas-medicion/${id}`, { method:'DELETE' }),

  // Tarifas base
  getTarifas:           async ()       => fetchApi(`${await pubCfg()}/tarifas`),
  crearTarifa:          async (dto)    => fetchApi(`${await pubCfg()}/tarifas`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarTarifa:     async (id,dto) => fetchApi(`${await pubCfg()}/tarifas/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarTarifa:       async (id)     => fetchApi(`${await pubCfg()}/tarifas/${id}`, { method:'DELETE' }),

  // Formatos
  getFormatos:          async (canal)  => fetchApi(`${await pubCfg()}/formatos${canal?`?canal=${canal}`:''}`),
  crearFormato:         async (dto)    => fetchApi(`${await pubCfg()}/formatos`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarFormato:    async (id,dto) => fetchApi(`${await pubCfg()}/formatos/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarFormato:      async (id)     => fetchApi(`${await pubCfg()}/formatos/${id}`, { method:'DELETE' }),

  // Ubicaciones
  getUbicaciones:       async (canal)  => fetchApi(`${await pubCfg()}/ubicaciones${canal?`?canal=${canal}`:''}`),
  crearUbicacion:       async (dto)    => fetchApi(`${await pubCfg()}/ubicaciones`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarUbicacion:  async (id,dto) => fetchApi(`${await pubCfg()}/ubicaciones/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarUbicacion:    async (id)     => fetchApi(`${await pubCfg()}/ubicaciones/${id}`, { method:'DELETE' }),

  // Recargos cromatismo
  getCromatismo:        async ()       => fetchApi(`${await pubCfg()}/cromatismo`),
  crearCromatismo:      async (dto)    => fetchApi(`${await pubCfg()}/cromatismo`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarCromatismo: async (id,dto) => fetchApi(`${await pubCfg()}/cromatismo/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarCromatismo:   async (id)     => fetchApi(`${await pubCfg()}/cromatismo/${id}`, { method:'DELETE' }),

  // Recargos edición
  getRecargosEdicion:   async ()       => fetchApi(`${await pubCfg()}/recargos-edicion`),
  crearRecargoEdicion:  async (dto)    => fetchApi(`${await pubCfg()}/recargos-edicion`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarRecargoEdicion: async (id,dto) => fetchApi(`${await pubCfg()}/recargos-edicion/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarRecargoEdicion:   async (id)     => fetchApi(`${await pubCfg()}/recargos-edicion/${id}`, { method:'DELETE' }),

  // Tipos de contenido
  getTiposContenido:    async ()       => fetchApi(`${await pubCfg()}/tipos-contenido`),
  crearTipoContenido:   async (dto)    => fetchApi(`${await pubCfg()}/tipos-contenido`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarTipoContenido: async (id,dto) => fetchApi(`${await pubCfg()}/tipos-contenido/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarTipoContenido:   async (id)     => fetchApi(`${await pubCfg()}/tipos-contenido/${id}`, { method:'DELETE' }),

  // Parámetros fiscales
  getFiscal:            async ()       => fetchApi(`${await pubCfg()}/fiscal`),
  crearFiscal:          async (dto)    => fetchApi(`${await pubCfg()}/fiscal`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarFiscal:     async (id,dto) => fetchApi(`${await pubCfg()}/fiscal/${id}`, { method:'PUT', body: JSON.stringify(dto) }),

  // Plantillas
  getPlantillas:        async (canal)  => fetchApi(`${await pubCfg()}/plantillas${canal?`?canal=${canal}`:''}`),
  crearPlantilla:       async (dto)    => fetchApi(`${await pubCfg()}/plantillas`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarPlantilla:  async (id,dto) => fetchApi(`${await pubCfg()}/plantillas/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  eliminarPlantilla:    async (id)     => fetchApi(`${await pubCfg()}/plantillas/${id}`, { method:'DELETE' }),

  // Cotizador
  cotizar:              async (dto)    => fetchApi(`${await pubCfg()}/cotizar`, { method:'POST', body: JSON.stringify(dto) }),
};

export const publicidadService = {
  // Anunciantes
  getAnunciantes:       async ()       => fetchApi(`${await pubBase()}/anunciantes`),
  crearAnunciante:      async (dto)    => fetchApi(`${await pubBase()}/anunciantes`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarAnunciante: async (id,dto) => fetchApi(`${await pubBase()}/anunciantes/${id}`, { method:'PUT', body: JSON.stringify(dto) }),

  // Pautas
  getPautas:            async (estado) => fetchApi(`${await pubBase()}/pautas${estado?`?estado=${estado}`:''}`),
  getPauta:             async (id)     => fetchApi(`${await pubBase()}/pautas/${id}`),
  crearPauta:           async (dto)    => fetchApi(`${await pubBase()}/pautas`, { method:'POST', body: JSON.stringify(dto) }),
  actualizarPauta:      async (id,dto) => fetchApi(`${await pubBase()}/pautas/${id}`, { method:'PUT', body: JSON.stringify(dto) }),
  cambiarEstado:        async (id,dto) => fetchApi(`${await pubBase()}/pautas/${id}/estado`, { method:'PATCH', body: JSON.stringify(dto) }),
  recalcularPrecio:     async (id)     => fetchApi(`${await pubBase()}/pautas/${id}/recalcular`, { method:'POST' }),
  duplicarPauta:        async (id, anuncianteId) => fetchApi(`${await pubBase()}/pautas/${id}/duplicar?anuncianteId=${anuncianteId}`, { method:'POST' }),

  // Material
  subirMaterial:        async (pautaId,dto) => fetchApi(`${await pubBase()}/pautas/${pautaId}/material`, { method:'POST', body: JSON.stringify(dto) }),
  revisarMaterial:      async (matId,dto)   => fetchApi(`${await pubBase()}/material/${matId}/revision`, { method:'PATCH', body: JSON.stringify(dto) }),
  eliminarMaterial:     async (matId)       => fetchApi(`${await pubBase()}/material/${matId}`, { method:'DELETE' }),
  marcarPrincipal:      async (pautaId,matId) => fetchApi(`${await pubBase()}/pautas/${pautaId}/material/${matId}/principal`, { method:'PATCH' }),

  // Programación
  programarEnEdicion:   async (dto)    => fetchApi(`${await pubBase()}/programacion`, { method:'POST', body: JSON.stringify(dto) }),
  desactivarProgramacion: async (id)   => fetchApi(`${await pubBase()}/programacion/${id}`, { method:'DELETE' }),
  getEdicionesDePauta:  async (pautaId) => fetchApi(`${await pubBase()}/pautas/${pautaId}/ediciones`),

  // Métricas
  getMetricas:          async (pautaId,desde,hasta) => fetchApi(`${await pubBase()}/pautas/${pautaId}/metricas?desde=${desde}&hasta=${hasta}`),
  registrarEvento:      async (pautaId,dto) => fetchApi(`${await pubBase()}/pautas/${pautaId}/evento`, { method:'POST', body: JSON.stringify(dto) }),

  // Público
  getPautasActivas:     async (posicion, edicionId) => {
    const base = await pubBase();
    // posicion puede ser null (traer todas). edicionId puede ser null también.
    const params = new URLSearchParams();
    if (posicion)  params.set('posicion',  posicion);
    if (edicionId) params.set('edicionId', edicionId);
    const q = params.toString();
    return fetchApi(`${base}/activas${q ? '?' + q : ''}`);
  },
};

// ═══════════════════════════════════════════════════════════════════
// Colaboración interna: comentarios por artículo (A) + chat 1-a-1 (B)
// ═══════════════════════════════════════════════════════════════════

/** Comentarios contextuales por artículo. */
export const comentariosService = {
  listar: async (articuloId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/articulos/${articuloId}/comentarios`),

  crear: async (articuloId, texto, parentId = null) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/articulos/${articuloId}/comentarios`, {
      method: 'POST',
      body: JSON.stringify({ texto, parentId }),
    }),

  marcarLeidos: async (articuloId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/articulos/${articuloId}/comentarios/marcar-leidos`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** [{ articuloId, cantidad }] — para badge en la lista de artículos. */
  noLeidos: async () =>
    fetchApi(`/periodicos/${PERIODICO_ID}/comentarios/no-leidos`),
};

/** Chat 1-a-1 entre usuarios del staff. */
export const chatService = {
  listarUsuarios: async (q = '') =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/usuarios${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  listarConversaciones: async () =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/conversaciones`),

  /** Abre (o crea si no existe) la conversación con otro usuario. Idempotente. */
  abrirConversacion: async (otroUsuarioId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/conversaciones`, {
      method: 'POST',
      body: JSON.stringify({ otroUsuarioId }),
    }),

  listarMensajes: async (convId, limite = 100) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/conversaciones/${convId}/mensajes?limite=${limite}`),

  enviar: async (convId, texto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/conversaciones/${convId}/mensajes`, {
      method: 'POST',
      body: JSON.stringify({ texto }),
    }),

  marcarLeidos: async (convId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/conversaciones/${convId}/leer`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  /** Total no-leídos globales (para badge en el botón flotante). */
  noLeidosTotal: async () =>
    fetchApi(`/periodicos/${PERIODICO_ID}/chat/no-leidos-total`),
};

// ═══════════════════════════════════════════════════════════════════
// Maquetación impresa: páginas, bloques, plantillas y export PDF
// ═══════════════════════════════════════════════════════════════════

export const maquetaService = {
  // ── Contenido dinámico (bloques auto-generados) ──
  listarTiposDinamicos: async () =>
    fetchApi(`/contenido-dinamico/tipos`),

  resolverDinamico: async (edicionId, elementoData) =>
    fetchApi(`/contenido-dinamico/resolver`, {
      method: 'POST',
      body: JSON.stringify({ edicionId, elementoData }),
    }),

  // Páginas de una edición
  listarPaginas: async (edicionId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/paginas`),

  crearPagina: async (edicionId, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/paginas`, {
      method: 'POST', body: JSON.stringify(dto),
    }),

  obtenerPagina: async (paginaId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/paginas/${paginaId}`),

  actualizarPagina: async (paginaId, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/paginas/${paginaId}`, {
      method: 'PUT', body: JSON.stringify(dto),
    }),

  eliminarPagina: async (paginaId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/paginas/${paginaId}`, {
      method: 'DELETE',
    }),

  aplicarPlantilla: async (paginaId, plantillaId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/paginas/${paginaId}/aplicar-plantilla/${plantillaId}`, {
      method: 'POST', body: JSON.stringify({}),
    }),

  // Bloques
  crearBloque: async (paginaId, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/paginas/${paginaId}/bloques`, {
      method: 'POST', body: JSON.stringify(dto),
    }),

  actualizarBloque: async (bloqueId, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/bloques/${bloqueId}`, {
      method: 'PUT', body: JSON.stringify(dto),
    }),

  eliminarBloque: async (bloqueId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/bloques/${bloqueId}`, {
      method: 'DELETE',
    }),

  // Plantillas
  listarPlantillas: async () =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/plantillas`),

  crearPlantilla: async (dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/plantillas`, {
      method: 'POST', body: JSON.stringify(dto),
    }),

  actualizarPlantilla: async (plantillaId, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/plantillas/${plantillaId}`, {
      method: 'PUT', body: JSON.stringify(dto),
    }),

  eliminarPlantilla: async (plantillaId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/maqueta/plantillas/${plantillaId}`, {
      method: 'DELETE',
    }),

  /** URL directa al PDF — modo 'rgb' o 'print'. Se abre en nueva pestaña. */
  urlExportarPdf: async (edicionId, modo = 'rgb') => {
    return `${API_BASE_URL}/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/pdf?modo=${modo}`;
  },

  /** Descarga el PDF de la maqueta. Va por fetch y no por window.open porque
   *  el endpoint exige autenticación: una pestaña nueva no lleva la cabecera
   *  Authorization y el backend responde 401. Devuelve un Blob. */
  descargarPdf: async (edicionId, modo = 'rgb') => {
    const token = localStorage.getItem('newspaperToken');
    const url = `${API_BASE_URL}/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/pdf`
              + `?modo=${encodeURIComponent(modo)}&_t=${Date.now()}`;
    const resp = await fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      let detalle = `HTTP ${resp.status}`;
      try { const e = await resp.json(); detalle = e.error || e.title || detalle; } catch {}
      throw new Error(detalle);
    }
    return await resp.blob();
  },

  // ─── Endpoints públicos (usados por el flipbook del lector) ────────

  /** Metadata pública: si la edición está publicada y tiene maqueta. */
  infoPublica: async (edicionId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/info`),

  /** Índice de contenido de la maqueta impresa: cada artículo colocado con su
   *  categoría y la primera página física donde aparece. Alimenta la guía de
   *  contenido cuando el flipbook está en modo impreso. */
  indicePublico: async (edicionId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/indice-publico`),

  /** Bloques interactivos de la maqueta (sopas de letras) con su página física
   *  y sus datos resueltos, para superponerlos sobre el PDF en el lector. */
  interactivosPublicos: async (edicionId) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/interactivos-publicos`),

  /** URL pública del PDF (RGB, sin marcas). Solo válida si la edición está
   *  publicada Y tiene maqueta. La consume el flipbook con pdf.js. */
  urlPdfPublico: (edicionId) => {
    return `${API_BASE_URL}/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/pdf-publico`;
  },

  /** URL del PDF de la versión web (A4 con portada, índice y artículos
   *  completos). No requiere maqueta impresa, cualquier edición publicada
   *  con artículos se puede descargar. */
  urlPdfWebPublico: (edicionId) => {
    return `${API_BASE_URL}/periodicos/${PERIODICO_ID}/ediciones/${edicionId}/maqueta/pdf-web-publico`;
  },
};

// ═══════════════════════════════════════════════════════════════════
// Plantillas de ARTÍCULO — layout INTERNO de un bloque de artículo
// (dónde va el título, la imagen, el resumen, el cuerpo, la firma).
// Distinta a las plantillas de PÁGINA (que definen la grilla de bloques).
// ═══════════════════════════════════════════════════════════════════
export const plantillaArticuloService = {
  listar: async (soloActivas = true) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/plantillas-articulo?soloActivas=${soloActivas}`),

  obtener: async (id) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/plantillas-articulo/${id}`),

  crear: async (dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/plantillas-articulo`, {
      method: 'POST', body: JSON.stringify(dto),
    }),

  actualizar: async (id, dto) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/plantillas-articulo/${id}`, {
      method: 'PUT', body: JSON.stringify(dto),
    }),

  eliminar: async (id) =>
    fetchApi(`/periodicos/${PERIODICO_ID}/plantillas-articulo/${id}`, {
      method: 'DELETE',
    }),
};
